const $$splitComponentImporter = () => import('/home/runner/work/zngly-corporate/zngly-corporate/wp-content/themes/zngly-corporate/frontend/src/routes/dash/settings/management/users/index.tsx?tsr-split=component');
import { lazyRouteComponent } from '@tanstack/react-router';
import { columnDefaults } from '@/Components/materialReactTable';
import { requireUserCaps } from '@/Router';
import { useAuthStore, useUserCaps } from '@/Utility/auth/Auth';
import { UsersManagementQuery, type Users_Management_Fragment$data, type Users_Management_Fragment$key, type Users_Management_Query } from '@/graphql/queries/User.query';
import { RelayEnvironment } from '@/graphql/relay-env';
import { usePaginationFragment } from '@/graphql/relay.utils';
import { faEye } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Add } from '@mui/icons-material';
import { IconButton, MenuItem, Tooltip } from '@mui/material';
import { createFileRoute, useElementScrollRestoration, useNavigate } from '@tanstack/react-router';
import { format as dateFormat } from 'date-fns';
import { type MRT_ColumnDef, type MRT_RowVirtualizer, MaterialReactTable, createMRTColumnHelper, useMaterialReactTable } from 'material-react-table';
import { useMemo, useRef, useState } from 'react';
import { loadQuery } from 'react-relay';
import type { DeepOmit, DeepWritable } from 'ts-essentials';
export const Route = createFileRoute('/dash/settings/management/users/')({
  component: lazyRouteComponent($$splitComponentImporter, 'component', () => Route.ssr),
  pendingComponent: () => <UsersTableComponent fallback />,
  beforeLoad: () => {
    requireUserCaps(['list_users'], {
      to: '/dash/settings/management/profile'
    });
  },
  loader: () => {
    return loadQuery<Users_Management_Query>(RelayEnvironment, UsersManagementQuery, {
      first: 24
    }, {
      fetchPolicy: 'store-or-network'
    });
  }
});
type RelayData = ReturnType<typeof usePaginationFragment<Users_Management_Query, Users_Management_Fragment$key>>;
type UsersTableComponentProps = {
  fallback?: boolean;
} & Partial<RelayData>;
type UserColumnNode = DeepOmit<DeepWritable<NonNullable<Users_Management_Fragment$data['users']>['edges'][number]['node']>, {
  properties: never;
  formEntries: never;
}>;
const colHelper = createMRTColumnHelper<UserColumnNode>();
const colDefaults = columnDefaults<UserColumnNode>();
const columns: MRT_ColumnDef<UserColumnNode>[] = [colHelper.accessor('id', {
  ...colDefaults.reset,
  ...colDefaults.noEdit,
  header: 'ID'
}), colHelper.accessor('email', {
  header: 'Email',
  ...colDefaults.noEdit
}), colHelper.accessor('name', {
  ...colDefaults.noEdit,
  header: 'Name'
}), colHelper.accessor('username', {
  header: 'Username',
  ...colDefaults.noEdit
}), colHelper.accessor('firstName', {
  header: 'First Name',
  ...colDefaults.noEdit
}), colHelper.accessor('lastName', {
  header: 'Last Name',
  ...colDefaults.noEdit
}), colHelper.accessor('role', {
  header: 'Role',
  Cell: ({
    row
  }) => {
    type UserRoles = 'user' | 'user_sales' | 'user_sales_restricted' | 'admin' | 'super_admin' | null | undefined;
    const role = row.original?.role as UserRoles;
    const prettyRole = (role: UserRoles) => {
      switch (role) {
        case 'user':
          return 'User';
        case 'user_sales':
          return 'Sales User';
        case 'user_sales_restricted':
          return 'Sales User (Restricted)';
        case 'admin':
          return 'Admin';
        case 'super_admin':
          return 'System Admin';
        default:
          return 'Unknown';
      }
    };
    return prettyRole(role);
  }
}), colHelper.accessor('registeredDate', {
  header: 'Date Registered',
  ...colDefaults.noEdit,
  Cell(props) {
    return dateFormat(new Date(props.row.original?.registeredDate ?? ''), 'EEE do MMM yy HH:mm:ss');
  }
})] as MRT_ColumnDef<UserColumnNode>[];
const UsersTableComponent = (props: UsersTableComponentProps) => {
  const [isMutating, setIsMutating] = useState(false);
  const canCreateUser = useUserCaps(['create_users']);
  const canEditUser = useUserCaps(['edit_users']);
  const userId = useAuthStore(state => state?.user?.id);
  const navigate = useNavigate();
  const isRefetching = useMemo(() => {
    return props?.fallback === true || props?.isRefetching === true;
  }, [props?.isRefetching, props?.fallback]);

  //called on scroll and possibly on mount to fetch more data as the user scrolls and reaches bottom of table
  const fetchMoreOnBottomReached = (containerRefElement?: HTMLDivElement | null) => {
    if (!containerRefElement) return;
    const {
      scrollHeight,
      scrollTop,
      clientHeight
    } = containerRefElement;

    //once the user has scrolled within 200px of the bottom of the table, fetch more data if we can
    if (scrollHeight - scrollTop - clientHeight < 200 && !props?.isLoadingNext && props?.hasNext && !props?.isRefetching) {
      props?.loadNext?.(24);
    }
  };
  const tableData = useMemo(() => {
    // first initial load
    if (props?.fallback === true && props?.data === null) {
      return [{
        id: 'loading',
        email: 'loading'
      }] as unknown as UserColumnNode[];
    }
    return (props?.data?.users?.edges ?? []).filter(edge => {
      !!edge?.node?.id;
      if (!edge?.node?.id) return false;

      // filter out zngly admin user roles
      if (['super_admin', 'api'].includes(edge?.node?.role ?? '') || ['super_admin', 'api', 'zngly-admin', 'zngly-user'].includes(edge?.node?.username ?? '') || ['admin@zngly.com', 'user@zngly.com', 'internal-user@zngly.com', 'internal-admin@zngly.com'].includes(edge?.node?.email ?? '')) {
        return false;
      }
      return true;
    }).map(({
      node
    }) => node) as unknown as UserColumnNode[];
  }, [props?.data?.users?.edges, props?.fallback]);

  // optionally access the underlying virtualizer instance
  const rowVirtualizerInstanceRef = useRef<MRT_RowVirtualizer>(null);

  // scroll restoration with tanstack/react-router
  // https://tanstack.com/router/v1/docs/framework/react/guide/scroll-restoration#manual-scroll-restoration
  const scrollRestorationId = 'dash-settings-management-users-table';
  const scrollEntry = useElementScrollRestoration({
    id: scrollRestorationId
  });

  // const tableRowCountText = useTableRowCountText({
  //   isLoadingNext: props?.isLoadingNext ?? false,
  //   tableDataLength: tableData.length,
  //   count: props?.data?.users?.count ?? 0,
  // });

  const table = useMaterialReactTable({
    columns,
    data: tableData,
    // 10,000 rows
    enablePagination: false,
    enableRowNumbers: true,
    enableRowVirtualization: true,
    renderTopToolbarCustomActions(props) {
      return <>
          <Tooltip title="Create User" enterDelay={250}>
            <span>
              <IconButton disabled={!canCreateUser} onClick={() => {
              navigate({
                to: '/dash/settings/management/users/create'
              });
            }}>
                <Add />
              </IconButton>
            </span>
          </Tooltip>
        </>;
    },
    enableRowActions: true,
    renderRowActionMenuItems: ({
      row,
      table
    }) => [<MenuItem key="view" disabled={row.original.id === userId} onClick={() => {
      navigate({
        to: '/dash/settings/management/users/$user',
        params: {
          user: row.original.id
        }
      });
    }}>
        <FontAwesomeIcon icon={faEye} style={{
        marginRight: '8px'
      }} /> View
      </MenuItem>],
    muiTablePaperProps(props) {
      return {
        sx: {
          width: '100%'
        }
      };
    },
    muiTableContainerProps: ({
      table
    }) => ({
      'data-scroll-restoration-id': scrollRestorationId,
      sx: {
        minHeight: '400px',
        maxHeight: `calc(100vh - ${table.refs.topToolbarRef.current?.offsetHeight}px - ${table.refs.bottomToolbarRef.current?.offsetHeight ?? 0}px - 160px)`
      },
      onScroll: event => fetchMoreOnBottomReached(event?.target as HTMLDivElement) //add an event listener to the table container element
    }),
    muiTableBodyRowProps: props => {
      return {
        onClick: event => {
          navigate({
            to: '/dash/settings/management/users/$user',
            params: {
              user: props.row.original.id
            }
          });
        },
        sx: {
          backgroundColor: props.row.index % 2 === 0 ? 'lightgray' : 'inherit',
          cursor: 'pointer'
        }
      };
    },
    // renderBottomToolbarCustomActions: (props) => (
    //   <>
    //     <Typography>{tableRowCountText}</Typography>
    //   </>
    // ),
    initialState: {
      columnPinning: {
        right: ['mrt-row-actions']
      },
      columnVisibility: {
        id: false,
        name: false
      }
    },
    state: {
      isLoading: isRefetching,
      isSaving: isMutating,
      showProgressBars: props?.isLoadingNext || isMutating
    },
    rowVirtualizerInstanceRef,
    // get access to the virtualizer instance
    rowVirtualizerOptions: {
      overscan: 10,
      initialOffset: scrollEntry?.scrollY
    }
  });
  return <MaterialReactTable table={table} />;
};